import { useEffect, useState } from "react";
import { CalendarCheck, CalendarX, Check, Globe, Info, LoaderCircle, Power } from "lucide-react";
import { authService } from "../../services/api";
import StatusPill from "../../components/StatusPill/StatusPill";
import { useNotification } from "../../components/Notifications/NotificationContext";

/**
 * Domínios da Spectrum para os redirecionadores: valem para todos os usuários.
 * Domínio novo entra ativo (o DNS já deve apontar para o servidor de redirect);
 * desativar tira o domínio dos links novos e para de servir os já gerados nele.
 */
const STATUS = {
    active: { label: "Ativo", color: "green" },
    inactive: { label: "Inativo", color: "gray" },
};

const errorOf = (err, fallback) => err?.response?.data?.error || fallback;
// 'AAAA-MM-DD' → 'DD/MM/AAAA' (sem fuso: é uma data, não um instante)
export const fmtDay = (iso) => (iso ? iso.split("-").reverse().join("/") : "");

export default function AdminDomains() {
    const api = authService.redirects;
    const { showError, showSuccess } = useNotification();
    const [domains, setDomains] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hostname, setHostname] = useState("");
    const [adding, setAdding] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [serverIps, setServerIps] = useState("");
    const [savingIps, setSavingIps] = useState(false);
    const [expiryDraft, setExpiryDraft] = useState({});

    useEffect(() => {
        let alive = true;
        api.admin_list_domains()
            .then(({ data }) => { if (alive) setDomains(data?.data || []); })
            .catch((err) => showError(errorOf(err, "Erro ao carregar os domínios.")))
            .finally(() => { if (alive) setLoading(false); });
        api.admin_get_settings()
            .then(({ data }) => { if (alive) setServerIps((data?.data?.server_ips || []).join(", ")); })
            .catch(() => {});
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const saveIps = async (e) => {
        e.preventDefault();
        if (savingIps) return;
        const ips = serverIps.split(",").map((ip) => ip.trim()).filter(Boolean);
        try {
            setSavingIps(true);
            const { data } = await api.admin_save_settings(ips);
            setServerIps((data?.data?.server_ips || []).join(", "));
            showSuccess("IP do servidor de redirect salvo.");
        } catch (err) {
            showError(errorOf(err, "Erro ao salvar o IP."));
        } finally {
            setSavingIps(false);
        }
    };

    const add = async (e) => {
        e.preventDefault();
        const value = hostname.trim();
        if (!value || adding) return;
        try {
            setAdding(true);
            const { data } = await api.admin_add_domain(value);
            setDomains((prev) => [...prev, data.data]);
            setHostname("");
            showSuccess(`${data.data.hostname} adicionado.`);
        } catch (err) {
            showError(errorOf(err, "Erro ao adicionar o domínio."));
        } finally {
            setAdding(false);
        }
    };

    const setExpiry = async (domain, value) => {
        try {
            setBusyId(domain.id);
            const { data } = await api.admin_set_domain_expiry(domain.id, value || null);
            setDomains((prev) => prev.map((d) => (d.id === domain.id ? data.data : d)));
            setExpiryDraft((prev) => ({ ...prev, [domain.id]: undefined }));
            showSuccess(value
                ? `${domain.hostname} expira em ${fmtDay(value)}. Os usuários veem o aviso de migração.`
                : `${domain.hostname} não expira mais.`);
        } catch (err) {
            showError(errorOf(err, "Erro ao salvar a expiração."));
        } finally {
            setBusyId(null);
        }
    };

    const setActive = async (domain, active) => {
        if (!active && !window.confirm(
            `Desativar ${domain.hostname}? Ele sai dos links novos e os links já gerados nele param de funcionar.`,
        )) return;
        try {
            setBusyId(domain.id);
            const { data } = await api.admin_set_domain_active(domain.id, active);
            setDomains((prev) => prev.map((d) => (d.id === domain.id ? data.data : d)));
            showSuccess(`${domain.hostname} ${active ? "ativado" : "desativado"}.`);
        } catch (err) {
            showError(errorOf(err, "Erro ao atualizar o domínio."));
        } finally {
            setBusyId(null);
        }
    };

    return (
        <>
            <form className="filters-section mt-4" onSubmit={saveIps}>
                <div className="filters-grid">
                    <div className="filter-group">
                        <label htmlFor="admin-server-ips" className="admin-domains-label">IP do servidor de redirect</label>
                        <input id="admin-server-ips" className="filter-input" placeholder="Ex.: 203.0.113.10"
                            autoComplete="off" spellCheck={false}
                            value={serverIps} onChange={(e) => setServerIps(e.target.value)} />
                    </div>
                    <button type="submit" className="btn-primary" disabled={savingIps}>
                        {savingIps ? "Salvando..." : "Salvar IP"}
                    </button>
                </div>
                <p className="admin-domains-hint">
                    <Info size={13} aria-hidden="true" /> Para onde o registro A do domínio próprio dos usuários aponta (aparece
                    para eles como instrução de DNS). Use um <b>IP reservado</b> (Reserved IP da DigitalOcean): ele fica com
                    você ao trocar de servidor, e nenhum usuário precisa refazer o DNS. Não depende de domínio — pode trocar os
                    domínios da Spectrum à vontade. Vários IPs: separe por vírgula.
                </p>
            </form>

            <form className="filters-section mt-4" onSubmit={add}>
                <div className="filters-grid">
                    <div className="filter-group">
                        <label htmlFor="admin-new-domain" className="sr-only">Novo domínio</label>
                        <input id="admin-new-domain" className="filter-input" placeholder="link.exemplo.com"
                            autoComplete="off" spellCheck={false} maxLength={253}
                            value={hostname} onChange={(e) => setHostname(e.target.value)} />
                    </div>
                    <button type="submit" className="btn-primary" disabled={adding}>
                        {adding ? "Adicionando..." : "Adicionar domínio"}
                    </button>
                </div>
                <p className="admin-domains-hint">
                    <Info size={13} aria-hidden="true" /> Aponte o DNS (registro A) do domínio para o servidor de
                    redirect antes de adicionar. O certificado HTTPS é emitido sozinho no primeiro acesso.
                </p>
            </form>

            {loading ? (
                <div className="loading mt-6">
                    <LoaderCircle size={15} className="animate-spin" aria-hidden="true" /> Carregando...
                </div>
            ) : domains.length === 0 ? (
                <div className="empty-state mt-6">
                    <Globe size={18} aria-hidden="true" />
                    <h4>Nenhum domínio cadastrado</h4>
                </div>
            ) : (
                <div className="admin-cards mt-6">
                    {domains.map((d) => {
                        const status = STATUS[d.status] || STATUS.inactive;
                        const isActive = d.status === "active";
                        return (
                            <div key={d.id} className="admin-card">
                                <div className="admin-card-head">
                                    <div className="admin-avatar"><Globe size={18} aria-hidden="true" /></div>
                                    <div className="admin-card-id">
                                        <div className="admin-card-name-row">
                                            <span className="admin-card-name">{d.hostname}</span>
                                        </div>
                                        <span className="admin-card-sub">
                                            {d.expires_at
                                                ? <span className="text-amber-300">Expira em {fmtDay(d.expires_at)} · usuários veem o aviso de migração</span>
                                                : "Disponível para todos os redirecionadores"}
                                        </span>
                                    </div>
                                    <StatusPill color={status.color}>{status.label}</StatusPill>
                                </div>
                                <div className="admin-card-footer">
                                    <label className="sr-only" htmlFor={`expiry-${d.id}`}>{`Expira em (${d.hostname})`}</label>
                                    <input id={`expiry-${d.id}`} type="date" className="filter-input admin-domain-date"
                                        value={expiryDraft[d.id] ?? d.expires_at ?? ""}
                                        onChange={(e) => setExpiryDraft((prev) => ({ ...prev, [d.id]: e.target.value }))} />
                                    <button type="button" disabled={busyId === d.id || !(expiryDraft[d.id] ?? d.expires_at)}
                                        className="admin-icon-btn is-blue"
                                        aria-label={`Salvar expiração de ${d.hostname}`}
                                        onClick={() => setExpiry(d, expiryDraft[d.id] ?? d.expires_at)}>
                                        <CalendarCheck size={14} aria-hidden="true" /><span>Salvar data</span>
                                    </button>
                                    {d.expires_at && (
                                        <button type="button" disabled={busyId === d.id}
                                            className="admin-icon-btn"
                                            aria-label={`Remover expiração de ${d.hostname}`}
                                            onClick={() => setExpiry(d, null)}>
                                            <CalendarX size={14} aria-hidden="true" /><span>Não expira</span>
                                        </button>
                                    )}
                                    <button type="button" disabled={busyId === d.id}
                                        className={`admin-icon-btn ${isActive ? "is-danger" : "is-success"}`}
                                        aria-label={`${isActive ? "Desativar" : "Ativar"} ${d.hostname}`}
                                        onClick={() => setActive(d, !isActive)}>
                                        {isActive ? <Power size={14} aria-hidden="true" /> : <Check size={14} aria-hidden="true" />}
                                        <span>{isActive ? "Desativar" : "Ativar"}</span>
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </>
    );
}
